// @ts-nocheck
import { Card, Code, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { BarChartWidget } from '../../../../components/advanced/Widgets';
import { mockBarChartData } from '../../mockData/widgets';

export default function BarChartWidgetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Bar Chart Widgets</Title>
        <Text c="dimmed" size="sm">
          Category comparison bar chart widgets. Supports vertical and horizontal orientations,
          grouped and stacked bars, and export functionality.
        </Text>
      </Stack>

      {/* Vertical Bar Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Vertical Bar Chart</Title>
          <Text size="sm" c="dimmed">
            Standard vertical bar chart for comparing values across categories.
          </Text>

          <BarChartWidget
            title="Tasks by Ministry"
            subtitle="Top 10 ministries by task count"
            data={mockBarChartData.slice(0, 10)}
            bars={[
              { dataKey: 'tasks', name: 'Total Tasks', color: '#228be6' },
            ]}
            orientation="vertical"
            height={350}
            showExport
          />

          <Code block>
{`<BarChartWidget
  title="Tasks by Ministry"
  data={taskData}
  bars={[
    { dataKey: 'tasks', name: 'Total Tasks', color: '#228be6' }
  ]}
  orientation="vertical"
  showExport
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Horizontal Bar Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Horizontal Bar Chart</Title>
          <Text size="sm" c="dimmed">
            Horizontal bars work better for long category names and rankings.
          </Text>

          <BarChartWidget
            title="Top Ministries"
            subtitle="Ranked by total tasks"
            data={mockBarChartData.slice(0, 8)}
            bars={[
              { dataKey: 'tasks', name: 'Total Tasks', color: '#40c057' },
            ]}
            orientation="horizontal"
            height={350}
            showExport
          />

          <Code block>
{`<BarChartWidget
  title="Top Ministries"
  data={rankingData}
  bars={[
    { dataKey: 'tasks', name: 'Total Tasks', color: '#40c057' }
  ]}
  orientation="horizontal"
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Grouped Bar Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Grouped Bar Chart</Title>
          <Text size="sm" c="dimmed">
            Compare multiple metrics side-by-side for each category.
          </Text>

          <BarChartWidget
            title="Task Status by Ministry"
            subtitle="Completed vs Pending tasks"
            data={mockBarChartData.slice(0, 8)}
            bars={[
              { dataKey: 'completed', name: 'Completed', color: '#40c057' },
              { dataKey: 'pending', name: 'Pending', color: '#fab005' },
            ]}
            orientation="vertical"
            stacked={false}
            height={350}
            showExport
            showLegend
          />

          <Code block>
{`<BarChartWidget
  title="Task Status by Ministry"
  data={statusData}
  bars={[
    { dataKey: 'completed', name: 'Completed', color: '#40c057' },
    { dataKey: 'pending', name: 'Pending', color: '#fab005' }
  ]}
  stacked={false}
  showLegend
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Stacked Bar Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Stacked Bar Chart</Title>
          <Text size="sm" c="dimmed">
            Show part-to-whole relationships with stacked bars.
          </Text>

          <BarChartWidget
            title="Task Distribution"
            subtitle="Completed and pending tasks stacked"
            data={mockBarChartData.slice(0, 10)}
            bars={[
              { dataKey: 'completed', name: 'Completed', color: '#40c057' },
              { dataKey: 'pending', name: 'Pending', color: '#fab005' },
            ]}
            orientation="vertical"
            stacked
            height={350}
            showExport
            showLegend
          />

          <Code block>
{`<BarChartWidget
  title="Task Distribution"
  data={distributionData}
  bars={[
    { dataKey: 'completed', name: 'Completed', color: '#40c057' },
    { dataKey: 'pending', name: 'Pending', color: '#fab005' }
  ]}
  stacked
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Comparison Grid */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Vertical vs Horizontal Comparison</Title>
          <Text size="sm" c="dimmed">
            Same data shown in both orientations for different use cases.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <BarChartWidget
              title="Vertical Orientation"
              data={mockBarChartData.slice(0, 6)}
              bars={[
                { dataKey: 'tasks', name: 'Tasks', color: '#7950f2' },
              ]}
              orientation="vertical"
              height={300}
            />

            <BarChartWidget
              title="Horizontal Orientation"
              data={mockBarChartData.slice(0, 6)}
              bars={[
                { dataKey: 'tasks', name: 'Tasks', color: '#7950f2' },
              ]}
              orientation="horizontal"
              height={300}
            />
          </SimpleGrid>

          <Code block>
{`// Vertical
<BarChartWidget
  data={data}
  bars={[{ dataKey: 'tasks', name: 'Tasks', color: '#7950f2' }]}
  orientation="vertical"
/>

// Horizontal
<BarChartWidget
  data={data}
  bars={[{ dataKey: 'tasks', name: 'Tasks', color: '#7950f2' }]}
  orientation="horizontal"
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Category Comparison</Text>
              <Text size="xs" c="dimmed">
                Compare values across different categories, departments, or time periods at a glance.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Rankings & Leaderboards</Text>
              <Text size="xs" c="dimmed">
                Display top performers, best-selling products, or highest-traffic pages in order.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Part-to-Whole Analysis</Text>
              <Text size="xs" c="dimmed">
                Use stacked bars to show how different components contribute to a total value.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Multi-Metric Comparison</Text>
              <Text size="xs" c="dimmed">
                Grouped bars let you compare multiple metrics side-by-side for each category.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
